import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { useApAging } from '../../hooks/useBills';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { AsOfPicker } from '../../components/reports/PeriodFilter';
import { formatCurrency, todayIso } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/reports/ap-aging')({
  component: ApAgingPage,
});

function ApAgingPage() {
  const companyId = useActiveCompanyId();
  const [asOf, setAsOf] = useState(todayIso());
  const { data, isLoading, error } = useApAging(companyId, asOf);

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>A/P Aging</h1>
        <p className="muted">No active company. A/P aging is a per-company report.</p>
      </div>
    );
  }

  const rows = data?.rows ?? [];
  const totals = data?.totals;

  return (
    <div>
      <Link to="/reports" style={{ fontSize: 13 }}>← Reports</Link>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '8px 0 16px', flexWrap: 'wrap', gap: 12 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>A/P Aging</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <AsOfPicker asOf={asOf} onChange={setAsOf} />
        </div>
      </div>

      {isLoading && <p className="muted">Loading…</p>}
      {error && <p className="field-error">Failed to load aging report.</p>}

      {!isLoading && rows.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No open payables as of {asOf}.</p>
        </div>
      )}

      {rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Vendor</th>
              <th style={{ textAlign: 'right' }}>Current</th>
              <th style={{ textAlign: 'right' }}>1–30</th>
              <th style={{ textAlign: 'right' }}>31–60</th>
              <th style={{ textAlign: 'right' }}>61–90</th>
              <th style={{ textAlign: 'right' }}>91+</th>
              <th style={{ textAlign: 'right' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.vendorId}>
                <td style={{ fontWeight: 500 }}>{r.name}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.current)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.d1_30)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.d31_60)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.d61_90)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(r.d91_plus)}</td>
                <td style={{ textAlign: 'right', fontWeight: 600 }}>{formatCurrency(r.total)}</td>
              </tr>
            ))}
          </tbody>
          {totals && (
            <tfoot>
              <tr style={{ fontWeight: 700, borderTop: '2px solid var(--border)' }}>
                <td>Totals</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totals.current)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totals.d1_30)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totals.d31_60)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totals.d61_90)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totals.d91_plus)}</td>
                <td style={{ textAlign: 'right' }}>{formatCurrency(totals.total)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </div>
  );
}
